function objectValue(value) {
  return value && typeof value === "object" && !Array.isArray(value) ? value : {};
}

function clone(value) {
  if (value === undefined) return undefined;
  return JSON.parse(JSON.stringify(value));
}

function hasValue(value) {
  return value !== undefined && value !== null && !(typeof value === "string" && value.trim() === "");
}

function mergeMissing(target, defaults) {
  const output = { ...objectValue(target) };
  Object.entries(objectValue(defaults)).forEach(([key, value]) => {
    if (value && typeof value === "object" && !Array.isArray(value)) {
      output[key] = mergeMissing(output[key], value);
    } else if (!hasValue(output[key])) {
      output[key] = clone(value);
    }
  });
  return output;
}

export const IMAGE_CAROUSEL_VISUAL_DEFAULTS = Object.freeze({
  shellStyle: Object.freeze({ backgroundColor: "#FFFFFF" }),
  eyebrowStyle: Object.freeze({ color: "#F35A3A", fontSize: 12, fontWeight: 900, lineHeight: 1.4, letterSpacing: 1.2 }),
  titleStyle: Object.freeze({ color: "#19242F", fontSizeDesktop: 40, fontSizeMobile: 30, fontWeight: 900, lineHeight: 1.08, letterSpacing: -0.4 }),
  subtitleStyle: Object.freeze({ color: "#5B5D73", fontSize: 16, fontWeight: 400, lineHeight: 1.65, maxWidth: 780 }),
  mediaStyle: Object.freeze({ backgroundColor: "rgba(25,36,47,.06)", borderColor: "transparent", borderWidth: 0, borderRadius: 28, boxShadow: "none", heightDesktop: 460, heightMobile: 280 }),
  captionStyle: Object.freeze({ color: "#FFFFFF", overlayColor: "rgba(0,0,0,.38)", fontSizeDesktop: 22, fontSizeMobile: 17, fontWeight: 800, lineHeight: 1.35, maxWidth: 900 }),
  arrowStyle: Object.freeze({ color: "#19242F", backgroundColor: "rgba(255,255,255,.92)", borderColor: "transparent", hoverColor: "#19242F", hoverBackgroundColor: "#FFFFFF", hoverBorderColor: "transparent", focusColor: "#F35A3A", size: 40, boxShadow: "0 8px 24px rgba(0,0,0,.12)" }),
  dotStyle: Object.freeze({ color: "rgba(255,255,255,.55)", activeColor: "#FFFFFF", hoverColor: "#FFFFFF", activeHoverColor: "#FFFFFF", height: 8, width: 8, activeWidth: 22 }),
});

export const TESTIMONIALS_VISUAL_DEFAULTS = Object.freeze({
  shellStyle: Object.freeze({ backgroundColor: "#EEF0F5" }),
  eyebrowStyle: Object.freeze({ color: "#F35A3A", fontSize: 12, fontWeight: 900, lineHeight: 1.4, letterSpacing: 1.2 }),
  titleStyle: Object.freeze({ color: "#19242F", fontSizeDesktop: 40, fontSizeMobile: 30, fontWeight: 900, lineHeight: 1.08, letterSpacing: -0.4 }),
  subtitleStyle: Object.freeze({ color: "#5B5D73", fontSize: 16, fontWeight: 400, lineHeight: 1.65, maxWidth: 780 }),
  cardStyle: Object.freeze({ backgroundColor: "#FFFFFF", borderColor: "rgba(25,36,47,.10)", borderWidth: 1, borderRadius: 24, boxShadow: "none", hoverBackgroundColor: "#FFFFFF", hoverBorderColor: "#F35A3A", hoverBoxShadow: "0 16px 40px rgba(25,36,47,.10)", hoverTranslateY: -2, paddingDesktop: 24, paddingMobile: 18 }),
  quoteStyle: Object.freeze({ color: "#19242F", fontSize: 16, fontWeight: 400, lineHeight: 1.7 }),
  nameStyle: Object.freeze({ color: "#19242F", fontSize: 16, fontWeight: 900, lineHeight: 1.3 }),
  roleStyle: Object.freeze({ color: "#5B5D73", fontSize: 14, fontWeight: 400, lineHeight: 1.4 }),
  accentStyle: Object.freeze({ color: "#F35A3A", fontSize: 46, fontWeight: 900, lineHeight: 0.8 }),
  avatarStyle: Object.freeze({ backgroundColor: "#F35A3A", color: "#FFFFFF", size: 48, borderColor: "transparent", borderWidth: 0, boxShadow: "none" }),
});

const CAROUSEL_LEGACY_MAP = Object.freeze({
  backgroundColor: ["shellStyle", "backgroundColor"],
  eyebrowColor: ["eyebrowStyle", "color"],
  titleColor: ["titleStyle", "color"],
  subtitleColor: ["subtitleStyle", "color"],
  captionColor: ["captionStyle", "color"],
  overlayColor: ["captionStyle", "overlayColor"],
  arrowBackgroundColor: ["arrowStyle", "backgroundColor"],
  arrowIconColor: ["arrowStyle", "color"],
  dotColor: ["dotStyle", "color"],
  activeDotColor: ["dotStyle", "activeColor"],
  heightDesktop: ["mediaStyle", "heightDesktop"],
  heightMobile: ["mediaStyle", "heightMobile"],
  borderRadius: ["mediaStyle", "borderRadius"],
});

const TESTIMONIALS_LEGACY_MAP = Object.freeze({
  backgroundColor: ["shellStyle", "backgroundColor"],
  eyebrowColor: ["eyebrowStyle", "color"],
  titleColor: ["titleStyle", "color"],
  subtitleColor: ["subtitleStyle", "color"],
  cardBackgroundColor: ["cardStyle", "backgroundColor"],
  cardBorderColor: ["cardStyle", "borderColor"],
  quoteColor: ["quoteStyle", "color"],
  nameColor: ["nameStyle", "color"],
  roleColor: ["roleStyle", "color"],
  accentColor: ["accentStyle", "color"],
  cardBorderRadius: ["cardStyle", "borderRadius"],
});

function materializeSection(raw, defaults, legacyMap) {
  const section = { ...objectValue(raw) };
  Object.entries(legacyMap).forEach(([legacyKey, [ownerKey, propertyKey]]) => {
    if (!hasValue(section[legacyKey])) return;
    section[ownerKey] = { ...objectValue(section[ownerKey]) };
    if (!hasValue(section[ownerKey][propertyKey])) section[ownerKey][propertyKey] = section[legacyKey];
  });
  Object.keys(defaults).forEach((ownerKey) => {
    section[ownerKey] = mergeMissing(section[ownerKey], defaults[ownerKey]);
  });
  Object.keys(legacyMap).forEach((legacyKey) => delete section[legacyKey]);
  return section;
}

export function materializeCarouselTestimonialsOwnership(settings = {}) {
  const source = clone(objectValue(settings));
  source.imageCarousel = materializeSection(source.imageCarousel, IMAGE_CAROUSEL_VISUAL_DEFAULTS, CAROUSEL_LEGACY_MAP);
  source.testimonials = materializeSection(source.testimonials, TESTIMONIALS_VISUAL_DEFAULTS, TESTIMONIALS_LEGACY_MAP);
  if (Array.isArray(source.layout?.segments)) {
    source.layout.segments = source.layout.segments.map((segment) => {
      if (!segment?.data || !["imageCarousel", "testimonials"].includes(segment?.type)) return segment;
      const data = clone(segment.data);
      if (segment.type === "imageCarousel") data.imageCarousel = materializeSection(data.imageCarousel, IMAGE_CAROUSEL_VISUAL_DEFAULTS, CAROUSEL_LEGACY_MAP);
      if (segment.type === "testimonials") data.testimonials = materializeSection(data.testimonials, TESTIMONIALS_VISUAL_DEFAULTS, TESTIMONIALS_LEGACY_MAP);
      return { ...segment, data };
    });
  }
  return source;
}
